import isNan from 'lodash/isNaN'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import { select } from 'redux-saga/effects'

import { materialToFormMap } from '../../../formSchemas'
import Region from '../../../libs/region-select/region'
import { OpeningGroup } from '../../../models/activeDrawable'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { DRAWABLE_TYPES, IMUP_WINDOW_AND_DOOR_TYPES } from '../../../shared/constants/drawable-types'
import IndexableObject from '../../../shared/constants/general-enums/indexableObject'
import { prepareValue } from '../../../shared/services/opening-form-services'
import { deckPorchOrFloorArea } from '../../../utils/formSchema/constants'
import { selectJoistDirectionOther } from '../../slices/common'
import {
    selectActiveChunkRegions,
    selectActiveChunkScalesAndDPI,
    selectActiveDocumentMapping,
    selectDocumentMappingIdIfActivePageIsOption,
    selectDocumentMappings,
} from '../../slices/documents'
import { selectAzureFeatureFlags } from '../../slices/flags'
import { ActiveGeometryGroup, selectActiveGeometryGroup } from '../../slices/geometry'
import { selectMasterSetPlanToCreate } from '../../slices/masterSetPlan'
import { selectProject } from '../../slices/project'
import { selectScaleFactor } from '../2D/createDrawableLocation'

/**
 * Tries to parse the input. If it is NaN -> default to null
 */
function tryParseFloat(input: string): number | null {
    const parsedInput = parseFloat(input)

    return !isNan(parsedInput) ? parsedInput : null
}

/**
 * Cleans the advanced settings object by removing properties with `undefined` or `false` values.
 * Retains first-level keys as empty objects if all nested properties are removed.
 *
 * @param {Object} advancedSettings - The advanced settings object to clean.
 *
 * @returns {Object} - The cleaned advanced settings object.
 */
function cleanAdvancedSettings(advancedSettings: IndexableObject) {
    return Object.entries(advancedSettings).reduce((acc, [key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const cleanedValue = cleanAdvancedSettings(value)

            acc[key] = Object.keys(cleanedValue).length > 0 ? cleanedValue : {} // keep as an empty object if all properties are removed
        } else if (value !== undefined && value !== false) {
            acc[key] = value
        } else if (typeof value === 'object' && value !== null) {
            acc[key] = {} // set to empty object if all values are removed
        }

        return acc
    }, {})
}

function* getMasterSetFloorLevel() {
    const activeDocumentMapping = yield select(selectActiveDocumentMapping)

    // If this is an option page, get the floor level from the parent (base home)
    if (activeDocumentMapping?.is_option && activeDocumentMapping?.additional_data?.estimatedBaseHomeId) {
        const documentMappings = yield select(selectDocumentMappings)
        const parentMapping = documentMappings?.find(
            (mapping) => mapping.id === activeDocumentMapping.additional_data.estimatedBaseHomeId
        )

        const pmFloorLevel = parentMapping?.additional_data?.floor_level

        if (pmFloorLevel) return pmFloorLevel
    } else {
        // For non-options or if no parent, use the current mapping's floor level
        const admFloorLevel = activeDocumentMapping?.additional_data?.floor_level

        if (admFloorLevel) return admFloorLevel
    }

    // Fall back to masterSetPlan if no floor level found in document mappings
    const masterSetPlan = yield select(selectMasterSetPlanToCreate)

    return masterSetPlan?.floorLevel
}

export function* prepareActiveDrawableBeforeSubmit(payload: { formData: OpeningGroup['settings'] }) {
    const activeDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)

    if (isNull(activeDrawableGroup)) return

    const scaleFactor: number = yield select(selectScaleFactor)

    const { dpi, pdfScale, xCalibrationFactor, yCalibrationFactor } = yield select(selectActiveChunkScalesAndDPI)

    const regions: Region[] = yield select(selectActiveChunkRegions)

    const potentialPageId: number | null = yield select(selectDocumentMappingIdIfActivePageIsOption)

    const prepareSettingsFromForm = materialToFormMap[activeDrawableGroup.type]?.onSubmit

    const prepareDrawables = materialToFormMap[activeDrawableGroup.type]?.prepareDrawables

    if (isUndefined(prepareSettingsFromForm)) return

    // Pass Azure Feature flags in the payload here.
    const azureFeatureFlags: AzureFeatureFlag[] | null = yield select(selectAzureFeatureFlags)
    const preparesSettings = prepareSettingsFromForm({}, payload.formData, azureFeatureFlags)

    // configuration_id is used here since gateway already supports it as a string
    // allowing for projects to be completed without any issues
    const settings = potentialPageId ? { ...preparesSettings, configuration_id: potentialPageId } : preparesSettings

    // Set bundle_floor_level to the floor level for Master Set projects with Floor Area materials
    const project = yield select(selectProject)

    if (project?.is_master_set && activeDrawableGroup.type === DRAWABLE_TYPES.FLOOR_AREA) {
        const floorLevel = yield getMasterSetFloorLevel()

        if (floorLevel) {
            settings.bundle_floor_level = floorLevel
        }
    }

    // since bundle fields are general, keep clean up general
    if (!deckPorchOrFloorArea.includes(settings.bundle_name)) {
        settings.bundle_location = null
        settings.bundle_floor_level = null
    }

    if (settings.bundle_floor_level !== 'other') {
        settings.bundle_other_floor_level = null
    }

    if (
        [
            DRAWABLE_TYPES.FLOOR_SYSTEM,
            DRAWABLE_TYPES.FLOOR_SYSTEMS,
            DRAWABLE_TYPES.ROOF_SYSTEM,
            DRAWABLE_TYPES.ROOF_SYSTEMS,
        ].includes(activeDrawableGroup.type)
    ) {
        if (prepareValue(settings.direction) === 'other') {
            const directionOther: string | null = yield select(selectJoistDirectionOther)

            if (directionOther) {
                settings.direction_other = directionOther
            }
        } else {
            settings.direction_other = null
        }
    }

    settings.linear_total = tryParseFloat(settings.linear_total)
    settings.quantity = tryParseFloat(settings.quantity)
    settings.total_quantity = tryParseFloat(settings.total_quantity)

    const activeGeometryGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)
    const openings = activeGeometryGroup && activeGeometryGroup.openings.filter((opening) => opening.isActive)

    const configuration = IMUP_WINDOW_AND_DOOR_TYPES.includes(activeDrawableGroup.type)
        ? settings.configuration ?? undefined
        : undefined

    const remarks = IMUP_WINDOW_AND_DOOR_TYPES.includes(activeDrawableGroup.type)
        ? settings.remarks ?? undefined
        : undefined

    if (IMUP_WINDOW_AND_DOOR_TYPES.includes(activeDrawableGroup.type) && settings.configuration) {
        delete settings.configuration
    }

    if (IMUP_WINDOW_AND_DOOR_TYPES.includes(activeDrawableGroup.type) && settings.remarks) delete settings.remarks

    const rawCleanedAdvancedSettings = cleanAdvancedSettings(settings.advanced_settings ?? {})

    const areAllSettingsEmptyObjects = Object.values(rawCleanedAdvancedSettings).every((value) => {
        return value === null || (typeof value === 'object' && Object.keys(value).length === 0)
    })

    // if advanced settings exist, clean them
    const preparedSettings = settings?.advanced_settings
        ? {
              ...settings,
              advanced_settings: areAllSettingsEmptyObjects
                  ? undefined
                  : cleanAdvancedSettings(settings.advanced_settings),
          }
        : settings

    return {
        activeDrawableGroup,
        prepareDrawables,
        openings,
        settings: JSON.parse(
            JSON.stringify(preparedSettings, (_, value) => (value === undefined || value === '' ? null : value))
        ),
        configuration,
        remarks,
        scaleFactor,
        dpi,
        pdfScale,
        xCalibrationFactor,
        yCalibrationFactor,
        regions,
    }
}
